import { isValidEmail } from '@lender/utils';

import { NEW_CONTACT_LIFECYCLE_STAGE, NEW_CONTACT_SOURCE } from './CrmContactSection.constants';

const MIN_PHONE_DIGITS = 10;

// CRM ids may serialize as a bare string or a value-object wrapper ({ value }).
export const getContactIdValue = contact => contact?.id?.value ?? contact?.id ?? null;

// A contact's email may arrive as `primaryEmail` (contact detail) or `email`
// (lighter list payloads) — accept either.
const getContactEmail = contact => contact?.primaryEmail ?? contact?.email ?? '';

const getBorrowerEmail = borrower => borrower?.email ?? borrower?.emailAddress ?? '';

// Secondary line under a contact's name in result rows: "role · org" when both
// are present, otherwise whichever exists, otherwise the email.
export const getContactSubtitle = contact => {
  const parts = [contact?.role, contact?.org].filter(Boolean);

  if (parts.length > 0) {
    return parts.join(' · ');
  }

  return getContactEmail(contact);
};

// The contacts list endpoint may return a bare array or a paginated envelope.
export const normalizeContactsResponse = data => {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.items ?? [];
};

// Search terms used to pull suggested contacts on mount: the borrower's full
// name and (when present) their email.
export const getSuggestionQueries = borrower => {
  const firstName = borrower?.firstName?.trim();
  const lastName = borrower?.lastName?.trim();
  const email = getBorrowerEmail(borrower).trim();

  const queries = [];

  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  if (fullName) {
    queries.push(fullName);
  }

  if (email) {
    queries.push(email);
  }

  return queries;
};

// Strong-match filter for suggestions: contact matches the borrower by
// first + last name OR by email (case-insensitive). De-duplicated by id.
export const getSuggestedContacts = (borrower, contacts) => {
  const firstName = borrower?.firstName?.trim().toLowerCase();
  const lastName = borrower?.lastName?.trim().toLowerCase();
  const email = getBorrowerEmail(borrower).trim().toLowerCase();

  const seen = new Set();

  return (contacts ?? []).filter(contact => {
    const id = getContactIdValue(contact);

    if (!id || seen.has(id)) {
      return false;
    }

    const nameMatch =
      !!firstName &&
      !!lastName &&
      contact.firstName?.trim().toLowerCase() === firstName &&
      contact.lastName?.trim().toLowerCase() === lastName;

    const emailMatch = !!email && getContactEmail(contact).trim().toLowerCase() === email;

    if (nameMatch || emailMatch) {
      seen.add(id);

      return true;
    }

    return false;
  });
};

export const getCreateContactPrefill = borrower => ({
  firstName: borrower?.firstName ?? '',
  lastName: borrower?.lastName ?? '',
  primaryEmail: getBorrowerEmail(borrower),
  primaryPhone: borrower?.phone ?? '',
});

export const isCreateContactValid = ({ firstName, lastName, primaryEmail }) =>
  !!firstName?.trim() && !!lastName?.trim() && isValidEmail(primaryEmail ?? '');

export const validateCreateContactForm = ({ firstName, lastName, primaryEmail, primaryPhone }) => {
  const errors = {};

  if (!firstName?.trim()) {
    errors.firstName = 'First name is required.';
  }

  if (!lastName?.trim()) {
    errors.lastName = 'Last name is required.';
  }

  const trimmedEmail = primaryEmail?.trim() ?? '';

  if (!trimmedEmail) {
    errors.primaryEmail = 'Email is required.';
  }

  if (trimmedEmail && !isValidEmail(trimmedEmail)) {
    errors.primaryEmail = 'Enter a valid email address.';
  }

  if (primaryPhone?.trim() && (primaryPhone.match(/\d/g) || []).length < MIN_PHONE_DIGITS) {
    errors.primaryPhone = 'Enter a valid phone number.';
  }

  return errors;
};

export const buildCreateContactPayload = form => ({
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  primaryEmail: form.primaryEmail.trim() || null,
  primaryPhone: form.primaryPhone.trim() || null,
  lifecycleStage: NEW_CONTACT_LIFECYCLE_STAGE,
  source: NEW_CONTACT_SOURCE,
});
